import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { PropsWithChildren, useContext } from 'react';
import types, {
  ProductDiscountType,
  ProductSkuType,
} from '../types/services-api';
import agent from '../agent';
import { GlobalContext } from './GlobalContext';
import analytics, {
  ADD_TO_CART_ACTION,
  ENGAGEMENT_CATEGORY,
  REMOVE_FROM_CART_ACTION,
} from '../analytics';
import dayjs from 'dayjs';
import { calculateFee } from '../utils/pricingHelper';
import { UserContext } from './UserContext';

export type SkuType =
  | 'BASIC'
  | 'JOB'
  | 'INV'
  | 'NAT'
  | 'DISTRO'
  | 'JOBSUB'
  | 'INVSUB'
  | 'NATSUB'
  | 'DISTROSUB';

export type ProductCartType = {
  selectedType: SkuType;
  qty: number;
  selectedAddOns: { [key: string]: number };
};

const STATIC_CART_DATA: ProductCartType = {
  selectedType: 'JOBSUB',
  qty: 1,
  selectedAddOns: {
    INV: 0,
    INVSUB: 0,
    NAT: 0,
    DISTRO: 0,
    NATSUB: 0,
    DISTROSUB: 0,
  },
};

export type PurchaseContextType = {
  appliedDiscount?: {
    code: string;
    message: string;
    productCode: string;
  };
  availableDiscounts: {
    [key: string]: types.ProductDiscountType[];
  };
  discountExpirations: { [key: string]: string };
  cart: ProductCartType;
  resetCart: () => void;
  updateCart: (productCode: SkuType, quantity: number) => void;
  setCart: React.Dispatch<SetStateAction<ProductCartType>>;
  products:
    | {
        [key: string]: ProductFormattedType;
      }
    | undefined;
  getBestDiscount: (props: {
    productCode: SkuType;
    cartUpdateQuantity: number;
    discounts?: { [key: string]: types.ProductDiscountType[] };
    endDate?: string;
  }) => types.ProductDiscountType | void;
  couponErrorMessage: string;
  getCatalog: (props: { discountCode?: string; role?: string }) => void;
  mapAnalyticsPayload: (
    sku: SkuType,
    index: number,
    addOn: boolean
  ) => { [key: string]: any } | undefined;
};

type ProductCombinedType = types.ProductSkuType & types.ProductDiscountType;
export type ProductFormattedType = ProductCombinedType | types.ProductSkuType;
export function isDiscountedType(obj: any): obj is ProductCombinedType {
  return !!obj && 'discountCode' in obj;
}

export const PurchaseContext = React.createContext<PurchaseContextType>({
  appliedDiscount: undefined,
  availableDiscounts: {},
  discountExpirations: {},
  cart: STATIC_CART_DATA,
  resetCart: () => {},
  updateCart: (productCode: SkuType, quantity: number) => {},
  setCart: () => {},
  products: undefined,
  getBestDiscount: (props: {
    productCode: string;
    cartUpdateQuantity: number;
    apiDiscounts?: { [key: string]: types.ProductDiscountType[] };
  }) => {},
  couponErrorMessage: '',
  getCatalog: (props: { discountCode?: string; role?: string }) => {},
  mapAnalyticsPayload: (sku: SkuType, index: number, addOn: boolean) =>
    undefined,
});

export function isOneMonthPromo(product?: ProductFormattedType) {
  return (
    isDiscountedType(product) &&
    (product.discountActivationFee < 0 ||
      !dayjs(product.endDate).isAfter(dayjs().add(30, 'day')))
  );
}

const PurchaseContextWrapper = (props: PropsWithChildren) => {
  const { user } = useContext(UserContext);
  const { urlParams } = useContext(GlobalContext);
  const [catalog, setCatalog] = useState<
    | {
        [key: string]: types.ProductSkuType;
      }
    | undefined
  >(undefined);
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    code: string;
    message: string;
    productCode: string;
  }>();
  const [discountExpirations, setDiscountExpirations] = useState<{
    [key: string]: string;
  }>({});
  const [availableDiscounts, setAvailableDiscounts] = useState<{
    [key: string]: types.ProductDiscountType[];
  }>({});

  const [products, setProducts] = useState<
    | {
        [key: string]: types.ProductSkuType | ProductFormattedType;
      }
    | undefined
  >(undefined);

  const [cart, setCart] = useState<ProductCartType>(STATIC_CART_DATA);

  function getCatalog(props: { role?: string; discountCode?: string }) {
    const code = !!props?.discountCode
      ? props.discountCode
      : (urlParams.discountCode ?? appliedDiscount?.code ?? undefined);
    setCouponErrorMessage('');
    setProducts(undefined);
    if (props?.role === 'employer') {
      getAuthCatalog(code);
    } else {
      getPublicCatalog(code);
    }
  }

  function getQty(sku: SkuType) {
    return ['JOB', 'JOBSUB'].includes(sku)
      ? cart.qty
      : cart.selectedAddOns[sku];
  }

  function getPublicCatalog(discountCode?: string) {
    agent.Jobs.publicCatalog(discountCode)
      .then((res) => {
        formatData(res.data, discountCode);
      })
      .catch((err) => {
        setCouponErrorMessage(err.message);
      });
  }

  function getAuthCatalog(discountCode?: string) {
    agent.Jobs.catalog(discountCode)
      .then((res) => {
        formatData(res.data, discountCode);
      })
      .catch((err) => {
        setCouponErrorMessage(err.message);
      });
  }

  function formatData(
    apiResponse: types.CatalogViewType,
    discountCode?: string
  ) {
    const apiCatalog = apiResponse.products;
    setDiscountExpirations(
      apiResponse.discounts.reduce((obj, curr) => {
        return {
          ...obj,
          [`${curr.productCode}-${curr.discountCode}`]: curr.endDate,
        };
      }, {})
    );
    const apiDiscounts = formatDiscountData(apiResponse.discounts);
    const displayDiscounts = apiResponse.discounts.filter(
      (obj) =>
        obj.discountCode.toLowerCase() === discountCode?.toLowerCase() ||
        !discountCode
    );
    if (!!displayDiscounts[0]) {
      setAppliedDiscount({
        code: displayDiscounts[0].discountCode,
        message: displayDiscounts[0].displayPhrase,
        productCode: displayDiscounts[0].productCode,
      });
    } else {
      setAppliedDiscount(undefined);
    }
    setAvailableDiscounts(apiDiscounts);
    setCatalog(formatProductData(apiCatalog));
    const formattedProducts = apiCatalog.map((product) => {
      const sku = product.productCode as SkuType;
      const qty = getQty(sku);
      if (!!apiDiscounts[product.productCode]) {
        const formattedProduct: ProductFormattedType = {
          ...product,
          ...getBestDiscount({
            productCode: sku,
            discounts: formatDiscountData(displayDiscounts),
            cartUpdateQuantity: qty,
          }),
        };
        return formattedProduct;
      } else {
        return product;
      }
    });
    setProducts(formatProductData(formattedProducts));
  }

  function formatProductData(productArray: ProductFormattedType[]): {
    [key: string]: types.ProductSkuType;
  } {
    const formattedData = productArray.reduce(
      (acc, item) => ({
        ...acc,
        [item.productCode]: item,
      }),
      {}
    );
    return formattedData;
  }

  function getBestDiscount(props: {
    productCode: SkuType;
    cartUpdateQuantity: number;
    discounts?: { [key: string]: types.ProductDiscountType[] };
    endDate?: string;
  }) {
    const { productCode, cartUpdateQuantity, endDate } = props;
    const discountsApplied = props.discounts
      ? props.discounts
      : availableDiscounts;
    const quantity =
      cartUpdateQuantity > 0
        ? cartUpdateQuantity
        : productCode.startsWith('INV')
          ? 25
          : 1;
    if (!!discountsApplied[props.productCode]) {
      const bestDiscount = discountsApplied[props.productCode].reduce(
        (
          prev: types.ProductDiscountType | undefined,
          curr: types.ProductDiscountType
        ) => {
          if (
            quantity >= curr.minimumQuantity &&
            quantity <= curr.maximumQuantity &&
            (!prev ||
              curr.discountFee + curr.discountActivationFee <
                prev.discountFee + prev.discountActivationFee) &&
            (!endDate || dayjs(curr.endDate).isAfter(dayjs(endDate)))
          ) {
            return curr;
          } else {
            return prev;
          }
        },
        undefined
      );
      return bestDiscount;
    }
  }

  function formatDiscountData(apiResponse: types.ProductDiscountType[]): {
    [key: string]: types.ProductDiscountType[];
  } {
    const formattedData = apiResponse.reduce(
      (acc, item) => {
        const sku = item.productCode;
        return {
          ...acc,
          [item.productCode]: [...(acc[sku] || []), item],
        };
      },
      {} as { [key: string]: types.ProductDiscountType[] }
    );
    return formattedData;
  }

  function mapAnalyticsPayload(sku: SkuType, index: number) {
    if (!!products) {
      const product = products[sku];
      const qty = getQty(sku);
      const payload = {
        index: index,
        item_id: sku,
        item_brand: 'DentalPost',
        item_name: product.description,
        coupon: isDiscountedType(product) ? product.discountCode : '',
        discount: isDiscountedType(product)
          ? (product.fee - product.discountFee) * qty
          : 0,
        item_category: sku.includes('SUB') ? 'Subscription' : '30-Day Posting',
        price: calculateFee(product),
        quantity: qty,
      };

      return payload;
    }
  }

  const prevCartRef = useRef<ProductCartType>(STATIC_CART_DATA);

  useEffect(() => {
    const prevCart = prevCartRef.current;
    if (!!products && cart.selectedType) {
      const product = products[cart.selectedType];
      const analyticsPayload = {
        currency: 'USD',
        value: calculateFee(product) * cart.qty,
        items: [
          {
            ...mapAnalyticsPayload(cart.selectedType, 0),
            item_list_id: 'purchase_form',
            item_list_name: 'Purchase Form',
          },
        ],
      };

      if (prevCart.qty < cart.qty) {
        analytics.Events.trackEvent({
          actionType: ADD_TO_CART_ACTION,
          category: ENGAGEMENT_CATEGORY,
          payload: analyticsPayload,
        });
      } else if (prevCart.qty > cart.qty) {
        analytics.Events.trackEvent({
          actionType: REMOVE_FROM_CART_ACTION,
          category: ENGAGEMENT_CATEGORY,
          payload: analyticsPayload,
        });
      }
    }

    prevCartRef.current = cart;
  }, [cart.qty]);

  function updateCart(sku: SkuType, qty: number) {
    if (['JOB', 'JOBSUB'].includes(sku)) {
      setCart((prevCart) => ({
        selectedType: sku,
        qty,
        selectedAddOns: prevCart.selectedAddOns,
      }));
    } else {
      setCart((prevCart) => ({
        ...prevCart,
        selectedAddOns: { ...prevCart.selectedAddOns, [sku]: qty },
      }));
    }

    //recalculate best discount when cart quantities change
    if (!!catalog && !!availableDiscounts) {
      const updatedProduct = {
        ...catalog[sku],
        ...getBestDiscount({
          productCode: sku,
          cartUpdateQuantity: qty,
          discounts: availableDiscounts,
        }),
      };
      setProducts((prevProducts) => ({
        ...(prevProducts ?? {}),
        [sku]: updatedProduct,
      }));
    }
  }

  function resetCart() {
    setCart({ ...STATIC_CART_DATA, selectedType: cart.selectedType });
  }

  //for DevTools to display context name
  PurchaseContext.displayName = 'Purchase Context';

  return (
    <PurchaseContext.Provider
      value={{
        appliedDiscount,
        availableDiscounts,
        discountExpirations,
        cart,
        resetCart,
        updateCart,
        setCart,
        products,
        getBestDiscount,
        couponErrorMessage,
        getCatalog,
        mapAnalyticsPayload,
      }}
    >
      {props.children}
    </PurchaseContext.Provider>
  );
};

export default PurchaseContextWrapper;
